import { create } from 'zustand';
import { authService } from '../services/auth';
import type { User, LoginCredentials, RegisterData } from '../types';
import { setSentryUser, setSentryAccount, clearSentryUser } from '../config/sentry';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;

  login: (credentials: LoginCredentials) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,

  login: async (credentials) => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.login(credentials);
      set({ user, isAuthenticated: true, isLoading: false });

      // Set Sentry user context
      setSentryUser({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
      });

      // Set Sentry account context if available
      if (user.account) {
        setSentryAccount({
          id: user.account.id,
          name: user.account.name,
          status: user.account.status,
          plan: user.account.subscription_plan?.name,
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error
        ? error.message
        : 'Login failed. Please check your credentials.';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  register: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const user = await authService.register(data);
      set({ user, isAuthenticated: true, isLoading: false });

      // Set Sentry user context
      setSentryUser({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
      });

      // Set Sentry account context if available
      if (user.account) {
        setSentryAccount({
          id: user.account.id,
          name: user.account.name,
          status: user.account.status,
          plan: user.account.subscription_plan?.name,
        });
      }
    } catch (error: unknown) {
      const message = error instanceof Error
        ? error.message
        : 'Registration failed. Please try again.';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  logout: async () => {
    try {
      await authService.logout();
    } finally {
      set({ user: null, isAuthenticated: false });
      // Clear Sentry user context
      clearSentryUser();
    }
  },

  fetchUser: async () => {
    set({ isLoading: true });
    try {
      const user = await authService.getUser();
      set({ user, isAuthenticated: true, isLoading: false });

      // Set Sentry user context
      setSentryUser({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
      });

      // Set Sentry account context if available
      if (user.account) {
        setSentryAccount({
          id: user.account.id,
          name: user.account.name,
          status: user.account.status,
          plan: user.account.subscription_plan?.name,
        });
      }
    } catch {
      set({ user: null, isAuthenticated: false, isLoading: false });
      // Clear Sentry context on failed fetch
      clearSentryUser();
    }
  },

  clearError: () => set({ error: null }),
}));

export default useAuthStore;
